/**
 * Admin Products API client
 *
 * Talks to /api/admin-products.php, /api/admin-retailers.php, and
 * /api/admin-colors.php. All requests are credentialed (session cookie).
 */

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

export interface AdminProduct {
  id: number;
  productId: string;
  sku: string;
  name: string;
  acronym: string;
  category: string;
  color: string;
  lengthFeet: number;
  sqft: number;
  description: string | null;
  // GS1 product codes (one set per product, no packaging levels).
  gs1Prefix: string | null;
  gtin: string | null;
  gtin8: string | null;
  upc: string | null;
  ean: string | null;
  isActive: boolean;
  msrpUsd: number | null;
  msrpCad: number | null;
  msrpActive: boolean | null;
  createdAt: string;
  updatedAt: string;
  imageCount?: number;
  metaCount?: number;
}

export interface AdminProductImage {
  id: number;
  url: string;
  altText: string | null;
  sortOrder: number;
  isPrimary: boolean;
}

export interface AdminProductMeta {
  id: number;
  key: string;
  value: string | null;
  sortOrder: number;
}

export interface AdminColor {
  id: number;
  slug: string;
  label: string;
  hexCode: string;
  sortOrder: number;
  isActive: boolean;
}

export interface AdminRetailerSummary {
  id: number;
  name: string;
  isNational: boolean;
  isActive: boolean;
}

export interface AdminProductsListResponse {
  products: AdminProduct[];
  retailers: AdminRetailerSummary[];
  colors: AdminColor[];
}

/** A Trusscore-default custom field value attached to a product. */
export interface AdminProductFieldValue {
  fieldDefId: number;
  value: string | null;
}

/** Field-definition shape echoed in product detail (subset used by the modal). */
export interface AdminProductFieldDef {
  id: number;
  fieldKey: string;
  label: string;
  dataType: 'numeric' | 'varchar' | 'url';
  isActive: boolean;
}

/** State of an external retailer's child row for a product (push coverage). */
export interface AdminRetailerCoverage {
  retailerId: number;
  isActive: boolean;
  needsReview: boolean;
}

export interface AdminProductDetail {
  product: AdminProduct;
  images: AdminProductImage[];
  meta: AdminProductMeta[];
  /** Trusscore-default (scope='trusscore') field definitions. */
  fieldDefs?: AdminProductFieldDef[];
  /** This product's Trusscore-default field values. */
  fieldValues?: AdminProductFieldValue[];
  /** External retailers (id != 1) that already carry this product. */
  retailerCoverage?: AdminRetailerCoverage[];
}

/** Result of pushing an existing product out to retailers. */
export interface PushProductResult {
  pushedCount: number;
  skippedCount: number;
  targetCount: number;
  pushedRetailerIds: number[];
  coverage: AdminRetailerCoverage[];
}

export interface ProductFieldsPatch {
  productId?: string;
  sku?: string;
  name?: string;
  acronym?: string;
  category?: string;
  color?: string;
  lengthFeet?: number;
  sqft?: number;
  description?: string | null;
  gs1Prefix?: string | null;
  gtin?: string | null;
  gtin8?: string | null;
  upc?: string | null;
  ean?: string | null;
  isActive?: boolean;
}

export interface ProductPatchPayload {
  product?: ProductFieldsPatch;
  msrp?: { priceUsd?: number; priceCad?: number; isActive?: boolean };
  images?: Array<{ url: string; altText?: string | null; sortOrder?: number; isPrimary?: boolean }>;
  meta?: Array<{ key: string; value?: string | null; sortOrder?: number }>;
  /** Trusscore-default custom field values to upsert (empty value deletes). */
  fieldValues?: AdminProductFieldValue[];
}

export interface ProductCreatePayload {
  product: Required<Pick<ProductFieldsPatch, 'productId' | 'sku' | 'name' | 'acronym' | 'category' | 'color'>> & ProductFieldsPatch;
  msrp: { priceUsd: number; priceCad: number };
  broadcast: { mode: 'none' | 'all' | 'subset'; retailerIds?: number[] };
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchAdminProducts(): Promise<AdminProductsListResponse> {
  const res = await fetch(`${API_BASE}/admin-products.php`, { credentials: 'include' });
  return handle<AdminProductsListResponse>(res);
}

export async function fetchAdminProduct(id: number): Promise<AdminProductDetail> {
  const res = await fetch(`${API_BASE}/admin-products.php?id=${id}`, { credentials: 'include' });
  return handle<AdminProductDetail>(res);
}

export async function createAdminProduct(payload: ProductCreatePayload): Promise<{ product: AdminProduct; broadcastCount: number }> {
  const res = await fetch(`${API_BASE}/admin-products.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle(res);
}

export async function patchAdminProduct(id: number, payload: ProductPatchPayload): Promise<AdminProductDetail> {
  const res = await fetch(`${API_BASE}/admin-products.php?id=${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle<AdminProductDetail>(res);
}

/**
 * Push an existing product out to other retailers. Retailers that already
 * carry it are skipped; new rows land inactive + needs_review at MSRP price.
 */
export async function pushProductToRetailers(
  id: number,
  payload: { mode: 'all' | 'subset'; retailerIds?: number[] },
): Promise<PushProductResult> {
  const res = await fetch(`${API_BASE}/admin-products.php?id=${id}&action=push`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle<PushProductResult>(res);
}

export async function deleteAdminProduct(id: number, confirmSku: string): Promise<void> {
  const params = new URLSearchParams({ id: String(id), confirmSku });
  const res = await fetch(`${API_BASE}/admin-products.php?${params}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Delete failed (${res.status})`);
  }
}

export async function fetchAdminColors(): Promise<AdminColor[]> {
  const res = await fetch(`${API_BASE}/admin-colors.php`, { credentials: 'include' });
  const data = await handle<{ colors: AdminColor[] }>(res);
  return data.colors;
}

// ============================================================================
// Bulk import / export
// ============================================================================

/** One product row as returned by the master export endpoint. */
export interface ExportProduct {
  id: number;
  productId: string;
  sku: string;
  name: string;
  acronym: string;
  category: string;
  color: string;
  lengthFeet: number;
  sqft: number;
  description: string | null;
  gs1Prefix: string | null;
  gtin: string | null;
  gtin8: string | null;
  upc: string | null;
  ean: string | null;
  isActive: boolean;
  msrpUsd: number | null;
  msrpCad: number | null;
}

export interface ImportError {
  row: number;
  id?: number | null;
  sku?: string | null;
  message: string;
}

export interface ImportFieldChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface ImportMsrpChange {
  from: number | null;
  to: number;
}

export interface ImportProductChange {
  id: number;
  sku: string;
  name: string;
  fields: ImportFieldChange[];
  msrp: { usd?: ImportMsrpChange; cad?: ImportMsrpChange };
}

export interface ImportRetailerBump {
  retailerId: number;
  retailerName: string;
  sku: string;
  currency: 'USD' | 'CAD';
  from: number;
  to: number;
}

export interface MasterImportResult {
  summary: {
    rowCount: number;
    productsChanged: number;
    fieldChanges: number;
    priceChanges: number;
    retailerBumps: number;
    errorCount: number;
  };
  changes: ImportProductChange[];
  bumps: ImportRetailerBump[];
  errors: ImportError[];
  committed: boolean;
}

export interface MasterImportPayload {
  columns: string[];
  rows: Record<string, unknown>[];
  options?: { bumpMatchingRetailers?: boolean };
  commit: boolean;
}

/** Full catalog + MSRP pricing for the .xlsx export. */
export async function fetchMasterExport(): Promise<ExportProduct[]> {
  const res = await fetch(`${API_BASE}/admin-products.php?action=export`, { credentials: 'include' });
  const data = await handle<{ products: ExportProduct[] }>(res);
  return data.products;
}

/** Dry-run (commit:false) or apply (commit:true) a master import. */
export async function runMasterImport(payload: MasterImportPayload): Promise<MasterImportResult> {
  const res = await fetch(`${API_BASE}/admin-products.php?action=import`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle<MasterImportResult>(res);
}

// ---- Per-retailer price import/export --------------------------------------

export interface RetailerExportProduct {
  id: number;
  sku: string;
  name: string;
  acronym: string;
  color: string;
  lengthFeet: number;
  priceUsd: number;
  priceCad: number;
  isActive: boolean;
}

export interface RetailerExport {
  retailer: { id: number; name: string };
  products: RetailerExportProduct[];
}

export interface RetailerPriceChange {
  from: number;
  to: number;
  clearsMarkup: boolean;
}

export interface RetailerImportChange {
  id: number;
  sku: string;
  name: string;
  prices: { usd?: RetailerPriceChange; cad?: RetailerPriceChange };
}

export interface RetailerImportResult {
  summary: {
    rowCount: number;
    productsChanged: number;
    priceChanges: number;
    errorCount: number;
  };
  changes: RetailerImportChange[];
  errors: ImportError[];
  committed: boolean;
}

export async function fetchRetailerExport(retailerId: number): Promise<RetailerExport> {
  const res = await fetch(`${API_BASE}/admin-retailers.php?id=${retailerId}&action=export`, { credentials: 'include' });
  return handle<RetailerExport>(res);
}

export async function runRetailerImport(
  retailerId: number,
  payload: { columns: string[]; rows: Record<string, unknown>[]; commit: boolean },
): Promise<RetailerImportResult> {
  const res = await fetch(`${API_BASE}/admin-retailers.php?id=${retailerId}&action=import`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle<RetailerImportResult>(res);
}
